import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/partials/button/button';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import TableComponent from 'components/partials/table-component/table-component';
import type {
  TableAmountState,
  TableColumnDef,
} from 'components/partials/table-component/table-component';
import toast from 'components/partials/toast/toast';

import SelectFundsModal from './components/select-funds-modal';
import FundsFilters from './components/funds-filters';
import AppliedFiltersBar from './components/applied-filters-bar';
import { getFundsTableSettings } from './funds-table-settings';

import {
  filterFunds,
  hasAnyFundsFilter,
  removeFundsFilter,
} from './helpers/funds-filters.helpers';

import {
  useModelSpecialFunds,
  useCreateModelSpecialFund,
  useUpdateModelSpecialFund,
  useDeleteModelSpecialFund,
  aidSpecialFundsKeys,
} from '../../hooks/use-aid-special-funds';
import { useInvalidateAidResults } from '../../hooks/use-aid-results';
import { useFundsDraft } from './hooks/use-funds-draft';
import { usePersistedSorting } from './hooks/use-persisted-sorting';

import { useDictionaryOptions } from 'hooks/use-dictionary-options';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { FUNDS_EXCLUDED_AWARDING_TYPES } from './funds-step.constants';

import Icons from 'assets/icons';

import { EMPTY_FUNDS_FILTERS } from './funds-step.types';
import type {
  Fund,
  FundCandidate,
  FundsFilterValues,
} from './funds-step.types';

const PAGE_SIZE = 10;

const transformFundsWithSubRows = (funds: Fund[]): Record<string, unknown>[] =>
  funds.map((fund, fundIndex) => {
    const fundKey = fund.id || fund._tempId;
    const awards = fund.awards || [];

    return {
      ...fund,
      fundIndex,
      isParent: true,
      childCount: awards.length,
      subRows: awards.map((award, awardIndex) => ({
        id: `${fundKey}-award-${awardIndex}`,
        parentId: fundKey,
        fundIndex,
        awardIndex,
        name: `Award amount ${awardIndex + 1}`,
        previousYearAmount: award.previousYearAmount,
        previousYearRecipients: award.previousYearRecipients,
        currentYearAmount: award.currentYearAmount,
        awardingType: fund.awardingType,
        isParent: false,
      })),
    };
  });

interface FundsStepProps {
  clientId?: string | number;
  readOnly?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const FundsStep = forwardRef<unknown, FundsStepProps>(
  ({ clientId: clientIdProp, readOnly, onDirtyChange }, ref) => {
    const { clientId: clientIdParam, modelId } = useParams();
    const clientId = clientIdProp ?? clientIdParam;
    const currentUser = useCurrentUser() as unknown as {
      data?: { oktaUserId?: string };
    };
    const userId = currentUser?.data?.oktaUserId;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const { sortingParams, setSortingParams } = usePersistedSorting(
      modelId ?? ''
    );
    const [appliedFilters, setAppliedFilters] =
      useState<FundsFilterValues>(EMPTY_FUNDS_FILTERS);

    const { options: awardingTypeOptions, isLoading: isDictionaryLoading } =
      useDictionaryOptions({
        userId: userId ?? '',
        type: 'AWARDING_TYPE',
        enabled: !!userId,
        exclude: FUNDS_EXCLUDED_AWARDING_TYPES,
      });

    const fundsQuery = useModelSpecialFunds({
      userId: userId ?? '',
      modelId: modelId ?? '',
      enabled: !!modelId,
    });
    const serverFunds = fundsQuery.data as Fund[] | null | undefined;
    const isLoading = fundsQuery.isLoading;

    const queryClient = useQueryClient();
    const createFund = useCreateModelSpecialFund();
    const updateFund = useUpdateModelSpecialFund();
    const deleteFund = useDeleteModelSpecialFund();
    const invalidateAidResults = useInvalidateAidResults();

    const {
      funds,
      hasChanges,
      assignedFundIds,
      addFunds,
      removeFund,
      updateAwardingType,
      updateAwardAmount,
      reset,
      markForReinit,
      computeChanges,
    } = useFundsDraft(serverFunds, modelId);

    const visibleFunds = useMemo(
      () => filterFunds((funds ?? []) as Fund[], appliedFilters),
      [funds, appliedFilters]
    );

    const tableData = useMemo(
      () => transformFundsWithSubRows(visibleFunds),
      [visibleFunds]
    );

    const handleApplyFilters = useCallback((next: FundsFilterValues): void => {
      setAppliedFilters(next);
      setPageIndex(0);
      setExpanded({});
    }, []);

    const handleRemoveFilter = useCallback(
      (field: keyof FundsFilterValues, value?: string): void => {
        setAppliedFilters((prev) => removeFundsFilter(prev, field, value));
        setPageIndex(0);
        setExpanded({});
      },
      []
    );

    const handleClearAllFilters = useCallback((): void => {
      setAppliedFilters(EMPTY_FUNDS_FILTERS);
      setPageIndex(0);
      setExpanded({});
    }, []);

    const getSubRows = useCallback(
      (row: { subRows?: unknown[] }) => row.subRows || [],
      []
    );

    const getRowCanExpand = useCallback(
      (row: { original: { isParent?: boolean; subRows?: unknown[] } }) =>
        Boolean(
          row.original.isParent && (row.original.subRows?.length ?? 0) > 0
        ),
      []
    );

    const handleTableSetAmount = useCallback(
      (updater: (state: TableAmountState) => TableAmountState): void => {
        const next = updater({ pageSize, totalRecords: visibleFunds.length });

        setPageSize(next.pageSize);
        setPageIndex(0);
      },
      [visibleFunds.length, pageSize]
    );

    const handleSave = useCallback(async (): Promise<void> => {
      if (!hasChanges) return;

      try {
        const { toCreate, toUpdate, toDelete } = computeChanges() as {
          toCreate: Record<string, unknown>[];
          toUpdate: { id: string | number; [key: string]: unknown }[];
          toDelete: (string | number)[];
        };

        await Promise.all(
          toDelete.map((fundId) =>
            deleteFund.mutateAsync({
              userId: userId ?? '',
              modelId: modelId ?? '',
              fundId,
            })
          )
        );

        await Promise.all(
          toUpdate.map(({ id, ...data }) =>
            updateFund.mutateAsync({
              userId: userId ?? '',
              modelId: modelId ?? '',
              fundId: id,
              data,
            })
          )
        );

        await Promise.all(
          toCreate.map((data) =>
            createFund.mutateAsync({
              userId: userId ?? '',
              modelId: modelId ?? '',
              data,
            })
          )
        );

        await queryClient.invalidateQueries({
          queryKey: aidSpecialFundsKeys.model(modelId ?? ''),
        });

        await invalidateAidResults(userId ?? '', modelId ?? '');

        markForReinit();

        toast.success({
          title: 'Funds updated',
          message: 'Special funds have been saved successfully',
        });
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } } };

        toast.error({
          title: 'Error saving funds',
          message: err?.response?.data?.message || 'An error occurred',
        });
      }
    }, [
      hasChanges,
      computeChanges,
      markForReinit,
      queryClient,
      userId,
      modelId,
      createFund,
      updateFund,
      deleteFund,
      invalidateAidResults,
    ]);

    useImperativeHandle(
      ref,
      () => ({ save: handleSave, discard: reset, hasChanges }),
      [handleSave, reset, hasChanges]
    );

    useEffect(() => {
      onDirtyChange?.(hasChanges);
    }, [hasChanges, onDirtyChange]);

    useEffect(() => {
      setExpanded({});
      setPageIndex(0);
    }, [modelId]);

    const handleAddFunds = useCallback(
      (selected: FundCandidate[]): void => {
        addFunds(selected, awardingTypeOptions[0]?.value);
        setIsModalOpen(false);
      },
      [addFunds, awardingTypeOptions]
    );

    const columns = useMemo<TableColumnDef<Record<string, unknown>>[]>(
      () =>
        getFundsTableSettings({
          onDelete: removeFund,
          onAwardingTypeChange: updateAwardingType,
          onAwardAmountChange: updateAwardAmount,
          awardingTypeOptions,
          readOnly,
        }) as TableColumnDef<Record<string, unknown>>[],
      [
        removeFund,
        updateAwardingType,
        updateAwardAmount,
        awardingTypeOptions,
        readOnly,
      ]
    );

    if (!modelId) {
      return (
        <div className="py-8 text-center text-gray-600">
          Please save the model first to manage funds.
        </div>
      );
    }

    return (
      <div className="relative" data-testid="funds-step">
        {funds.length === 0 && !isLoading ? (
          <EmptySpaceContent
            customClass="h-[55vh]"
            text="No funds have been added yet"
            linkShow={!readOnly}
            linkText="Add funds"
            onClick={() => setIsModalOpen(true)}
            disabled={isDictionaryLoading}
          />
        ) : (
          <>
            <div className="flex items-center justify-between gap-5 mb-4">
              <h3 className="text-lg font-bold text-gray-700">
                Selected funds
              </h3>
              <FundsFilters
                values={appliedFilters}
                onApply={handleApplyFilters}
                buttonSize="sm"
              />
            </div>

            <AppliedFiltersBar
              values={appliedFilters}
              onRemove={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              className="mb-4"
            />

            <TableComponent
              columns={columns}
              data={tableData}
              isLoading={isLoading}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPagination={setPageIndex}
              setAmount={handleTableSetAmount}
              sortingParams={sortingParams}
              setSortingParams={setSortingParams}
              manualSorting={false}
              manualPagination={false}
              totalRecords={visibleFunds.length}
              pagination={visibleFunds.length > PAGE_SIZE}
              expandable
              expanded={expanded as never}
              onExpandedChange={setExpanded as never}
              getSubRows={getSubRows as never}
              getRowCanExpand={getRowCanExpand as never}
              customHeight="max-h-[50vh]"
              paginateExpandedRows={false}
            />

            {!visibleFunds.length && hasAnyFundsFilter(appliedFilters) && (
              <div
                className="text-center py-8 text-gray-500"
                data-testid="no-funds-match-filters"
              >
                No funds match the selected filters
              </div>
            )}

            {!readOnly && (
              <div className="mt-4">
                <Button
                  type="button"
                  variant="reserve-outlined"
                  size="sm"
                  onClick={() => setIsModalOpen(true)}
                  iconAfter={<Icons.Plus />}
                  className="shadow"
                  disabled={isDictionaryLoading}
                  data-testid="add-funds-button"
                >
                  Add funds
                </Button>
              </div>
            )}
          </>
        )}

        {isModalOpen && (
          <SelectFundsModal
            userId={userId ?? ''}
            clientId={clientId ?? ''}
            modelId={modelId ?? ''}
            excludeIds={assignedFundIds}
            onAdd={handleAddFunds}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
    );
  }
);

FundsStep.displayName = 'FundsStep';

export default FundsStep;
